import { CGetAllShortlistCandidates } from "../Services/ShortlistedCandidatesService";
import {
  viewResume,
  GetCandidateDetails,
} from "../../ConsultantAllJobs/Services/CAllJobsService";
import React, { useState, useEffect } from "react";
import { Eye, Funnel, FileText } from "lucide-react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ViewResumeModal from "../../../Employee/Jobs/SendCandidates/Modals/ViewResumeModal";
import ColumnFilterModal from "../../../Employee/CommonModals/ColumnFilterModal";
import Pagination from "../../../Employee/CommonModals/Pagination";
import NormalAlert from "../../../Alerts/NormalAlert";
import CandidateDetailsModal from "../../ConsultantAllJobs/Modal/CandidateDetailsModal";
import JobDescriptionModal from "../../../Employee/Jobs/DraftJob/Modals/JobDescriptionModal";

const ShortlistedCandidate = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [candidateDetails, setCandidateDetails] = useState(null);

  const [resumeUrl, setResumeUrl] = useState("");
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showJDModal, setShowJDModal] = useState(false);

  const [alert, setAlert] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  const [filters, setFilters] = useState({
    jobTitle: [],
  });

  useEffect(() => {
    const GetShortlistCandidates = async () => {
      try {
        const response = await CGetAllShortlistCandidates();
        if (response) {
          setCandidates(response.data.shortlistedCandidates);
        }
      } catch (error) {
        const backendMessage = error?.response?.data?.message;

        if (backendMessage !== "No candidate found.") {
          setAlert({
            type: "fail",
            message:
              backendMessage || "Something went wrong. Please try again.",
          });
        }

        setCandidates([]);
      } finally {
        setLoading(false);
      }
    };
    GetShortlistCandidates();
  }, []);

  const filterOptions = {
    jobTitle: [...new Set(candidates.map((c) => c.jobTitel))],
  };

  const filteredCandidates = candidates.filter((c) => {
    const selectedTitles = filters.jobTitle;

    const matchesFilter =
      selectedTitles.length === 0 || selectedTitles.includes(c.jobTitel);

    const matchesSearch = [
      c.name,
      c.jobTitel,
      c.email,
      c.mobile,
      c.submitedBy,
    ].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesFilter && matchesSearch;
  });

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;

  const currentCandidates = filteredCandidates.slice(indexOfFirst, indexOfLast);

  const handleResumeClick = async (candidate) => {
    if (!candidate.resumePath) {
      setAlert({ type: "fail", message: "Resume not available" });
      return;
    }

    const fileName = candidate.resumePath.split("/").pop();
    const url = await viewResume(fileName);

    setResumeUrl(url);
    setShowResumeModal(true);
  };

  const handleNameClick = async (candidate) => {
    try {
      const res = await GetCandidateDetails(candidate.submitedCandidateUID);

      if (res?.data?.status === "Success") {
        setCandidateDetails(res.data); // FULL DATA
        setSelectedCandidate(candidate); // BASIC DATA
        setShowCandidateModal(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <div className="w-full py-8 mt-12">
        <div className="max-w-[1240px] mx-auto">
          <div className="bg-white rounded-2xl px-3 sm:px-4 md:px-6 py-4 md:py-6 shadow-sm">
            {/* Header */}

            <div className="bg-master-header text-white rounded-xl px-6 py-3 mb-6">
              <h3 className="font-bold text-lg">Shortlist Candidates</h3>
            </div>

            {/* Search */}
            <div className="mb-6 flex justify-between items-center flex-wrap gap-4">
              <input
                type="text"
                placeholder="Search by name, title..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-full px-5 py-2 w-full max-w-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Table */}

            <div className="border rounded-lg overflow-x-auto overflow-y-visible relative">
              <table className="w-full min-w-[900px] text-center relative">
                <thead className="bg-[#f5f8ff] text-sm uppercase">
                  <tr>
                    <th className="py-2 md:py-3 text-xs md:text-sm">#</th>

                    <th className="py-2 md:py-3 text-xs md:text-sm">
                      <div className="flex justify-center items-center gap-2">
                        Job Title
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("jobTitle")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-2 md:py-3 text-xs md:text-sm">
                      Submitted By
                    </th>

                    <th className="py-2 md:py-3 text-xs md:text-sm">Name</th>

                    <th className="py-2 md:py-3 text-xs md:text-sm">Email</th>

                    <th className="py-2 md:py-3 text-xs md:text-sm">Mobile</th>

                    <th className="py-2 md:py-3 text-xs md:text-sm">Status</th>

                    <th className="py-2 md:py-3 text-xs md:text-sm">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <SkeletonTheme baseColor="#e5e7eb" highlightColor="#f3f4f6">
                      {[...Array(5)].map((_, rowIdx) => (
                        <tr key={rowIdx}>
                          {[...Array(9)].map((__, colIdx) => (
                            <td key={colIdx} className="py-4">
                              <Skeleton height={20} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </SkeletonTheme>
                  ) : currentCandidates.length ? (
                    currentCandidates.map((c, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          {indexOfFirst + idx + 1}
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          {c.jobTitel || "-"}
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          {c.submitedBy} ({c.submitByType})
                        </td>

                        <td
                          className="text-blue-600 cursor-pointer py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px] font-semibold"
                          onClick={() => handleNameClick(c)}
                        >
                          {c.name || "-"}
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          {c.email || "-"}
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          {c.mobile || "-"}
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          {c.status || "-"}
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          <button
                            onClick={() => handleResumeClick(c)}
                            className="p-1 hover:bg-gray-100 rounded-full"
                          >
                            <FileText size={18} className="text-primary" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="py-6 text-red-500 text-center">
                        No Candidate Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}

            <Pagination
              totalItems={filteredCandidates.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setItemsPerPage}
            />
          </div>
        </div>
      </div>

      <CandidateDetailsModal
        show={showCandidateModal}
        onClose={() => setShowCandidateModal(false)}
        candidateDetails={candidateDetails}
        selectedCandidate={selectedCandidate}
        onViewSystemRemark={() => {}}
        showSystemRemarkButton={false}
        showResumeButton={true}
        onViewResume={() => handleResumeClick(selectedCandidate)}
        onViewJD={() => setShowJDModal(true)}
      />

      {/* Column Filter */}
      <ColumnFilterModal
        show={!!activeFilter}
        onHide={() => setActiveFilter(null)}
        title={activeFilter}
        items={filterOptions[activeFilter] || []}
        filteredItems={(filterOptions[activeFilter] || []).filter((item) =>
          item?.toLowerCase().includes(filterSearch.toLowerCase()),
        )}
        selectedItems={filters[activeFilter] || []}
        setSelectedItems={(values) =>
          setFilters((prev) => ({
            ...prev,
            [activeFilter]: values,
          }))
        }
        searchText={filterSearch}
        setSearchText={setFilterSearch}
      />

      {/* Resume Modal */}

      <ViewResumeModal
        show={showResumeModal}
        resumeUrl={resumeUrl}
        onClose={() => setShowResumeModal(false)}
      />

      <JobDescriptionModal
        show={showJDModal}
        onHide={() => setShowJDModal(false)}
        fullPath={selectedCandidate?.descreption}
      />

      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default ShortlistedCandidate;
